import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MaintenanceEvent } from '~/api/types'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/shared/empty-state'
import {
    buildImpactReportBody,
    REPORT_STYLE,
} from '~/features/maintenance/impact-report-window'
import type { ImpactRow } from '~/features/maintenance/types'

const STORAGE_KEY = 'haccp-maint-report'

type ReportHandoff = {
    event: MaintenanceEvent | null
    rows: ImpactRow[]
    print: boolean
}

/** 로딩·데이터없음 등 비-보고서 상태를 앱 디자인에 맞춰 중앙 정렬로 감싼다(맨 화면 방지). */
function ReportShell({ children }: { children: React.ReactNode }) {
    return (
        <div className='bg-background flex min-h-screen items-center justify-center p-6'>
            <div className='w-full max-w-md'>{children}</div>
        </div>
    )
}

/**
 * 변경 영향 분석 보고서 — 앱 크롬 없이 standalone 으로 렌더한다(인쇄/PDF 친화).
 * 유지하기 화면에서 localStorage(`haccp-maint-report`)로 넘긴 데이터를 재사용해 본문을 만든다.
 * 데이터를 지우지 않아 새로고침·재오픈에도 마지막 보고서가 유지된다(다음 열람 시 덮어씀).
 * print 플래그면 자동 인쇄(PDF 저장)로 이어준다.
 */
export function MaintenanceReportPage() {
    const navigate = useNavigate()
    const [body, setBody] = useState<string | null>(null)
    const [shouldPrint, setShouldPrint] = useState(false)
    const [ready, setReady] = useState(false)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            try {
                const data = JSON.parse(raw) as ReportHandoff
                setBody(
                    buildImpactReportBody(data.event ?? null, data.rows ?? [])
                )
                setShouldPrint(!!data.print)
            } catch (err) {
                // 데이터는 있는데 렌더에 실패한 경우 — '데이터 없음'과 구분해 표기·기록한다.
                console.error('변경 영향 보고서를 표시하지 못했습니다.', err)
                setFailed(true)
            }
        }
        setReady(true)
    }, [])

    // 데이터가 준비되면 자동 인쇄 다이얼로그(사용자가 PDF로 저장).
    useEffect(() => {
        if (body && shouldPrint) {
            const t = setTimeout(() => window.print(), 400)
            return () => clearTimeout(t)
        }
    }, [body, shouldPrint])

    if (!ready) {
        return (
            <ReportShell>
                <EmptyState title='보고서를 준비하고 있어요…' />
            </ReportShell>
        )
    }
    if (failed) {
        return (
            <ReportShell>
                <EmptyState
                    title='보고서를 표시하지 못했어요'
                    description='변경 검토 화면에서 다시 열어 주세요. 문제가 계속되면 관리자에게 알려 주세요.'
                    action={
                        <Button onClick={() => navigate('/maintenance')}>
                            변경 검토로 가기
                        </Button>
                    }
                />
            </ReportShell>
        )
    }
    if (!body) {
        return (
            <ReportShell>
                <EmptyState
                    title='보고서 데이터를 찾을 수 없어요'
                    description='변경 검토 화면에서 변경 분석을 실행한 뒤 다시 열어 주세요.'
                    action={
                        <Button onClick={() => navigate('/maintenance')}>
                            변경 검토로 가기
                        </Button>
                    }
                />
            </ReportShell>
        )
    }

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: REPORT_STYLE }} />
            <div dangerouslySetInnerHTML={{ __html: body }} />
        </>
    )
}
